import { Navigate, useRoutes } from 'react-router-dom';

import LoginPage from './pages/login.page';
import { Layout as DashboardLayout } from './components/layouts/dashboard.layout';
import OverviewPage from './pages/dashboard/overview.page';
import UserListPage from './pages/dashboard/user-list.page';
import UserDetailPage from './pages/dashboard/user-detail.page';
import ProductListPage from './pages/dashboard/product-list.page';
import ProductDetailPage from './pages/dashboard/product-detail.page';
import ProductCategoryListPage from './pages/dashboard/product-category-list.page';
import ServiceListPage from './pages/dashboard/service-list.page';
import ServiceDetailPage from './pages/dashboard/service-detail.page';
import CustomerListPage from './pages/dashboard/customer-list.page';
import CustomerDetailPage from './pages/dashboard/customer-detail.page';
import OrderListPage from './pages/dashboard/order-list.page';
import DeliveryOrderQueuePage from './pages/dashboard/delivery-order-queue.page';
import SettingsPage from './pages/dashboard/settings.page';
import ExpenseList from './pages/dashboard/expense-list.page';
import ExpenseDetailPage from './pages/dashboard/expense-detail.page';
import ReportPage from './pages/dashboard/report.page';
import CommissionReportPage from './pages/dashboard/commission-report.page';
import UserEventsPage from './pages/dashboard/user-events.page';
import ShopListPage from './pages/dashboard/shop-list.page';
import ShopDetailPage from './pages/dashboard/shop-detail.page';
import LocationListPage from './pages/dashboard/location-list.page';
import { useShopDetails } from './hooks/use-shop';
import PrivacyPolicy from './pages/legal/privacy-policy';
import TermsOfService from './pages/legal/terms-of-service';

/**
 * Services only exist for salon locations. Hiding the sidebar entry is not
 * enough — the pages stay reachable by URL, browser history, or a bookmark made
 * before the location changed kind — so the routes themselves redirect.
 */
function SalonOnly({ children }: { children: JSX.Element }): JSX.Element {
	const { isRestaurant } = useShopDetails();
	return isRestaurant ? <Navigate to="/dashboard/products" replace /> : children;
}

export default function Router() {
	const routes = useRoutes([
		{
			path: '/dashboard',
			element: <DashboardLayout />,
			children: [
				{ element: <Navigate to="/dashboard/users" />, index: true },
				{ path: 'overview', element: <OverviewPage /> },
				{ path: 'report', element: <ReportPage /> },
				{ path: 'commission', element: <CommissionReportPage /> },
				{ path: 'user-events', element: <UserEventsPage /> },
				{ path: 'users', element: <UserListPage /> },
				{ path: 'users/new', element: <UserDetailPage /> },
				{ path: 'users/:id/edit', element: <UserDetailPage /> },
				{ path: 'products', element: <ProductListPage /> },
				{ path: 'products/new', element: <ProductDetailPage /> },
				{ path: 'products/:id/edit', element: <ProductDetailPage /> },
				{ path: 'product-categories', element: <ProductCategoryListPage /> },
				{ path: 'services', element: <SalonOnly><ServiceListPage /></SalonOnly> },
				{ path: 'services/new', element: <SalonOnly><ServiceDetailPage /></SalonOnly> },
				{ path: 'services/:id/edit', element: <SalonOnly><ServiceDetailPage /></SalonOnly> },
				{ path: 'customers', element: <CustomerListPage /> },
				{ path: 'customers/new', element: <CustomerDetailPage /> },
				{ path: 'customers/:id/edit', element: <CustomerDetailPage /> },
				{ path: 'orders', element: <OrderListPage /> },
				{ path: 'delivery-orders', element: <DeliveryOrderQueuePage /> },
				{ path: 'expenses', element: <ExpenseList /> },
				{ path: 'expenses/new', element: <ExpenseDetailPage /> },
				{ path: 'expenses/:id/edit', element: <ExpenseDetailPage /> },
				{ path: 'shops', element: <ShopListPage /> },
				{ path: 'shops/:id', element: <ShopDetailPage /> },
				{ path: 'locations', element: <LocationListPage /> },
				{ path: 'settings', element: <SettingsPage /> },

			],
		},
		{
			path: 'login',
			element: <LoginPage />,
		},
		{
			path: '/',
			element: <LoginPage />,
		},
		{
			path: 'privacy-policy',
			element: <PrivacyPolicy />,
		},
		{
			path: 'terms-of-service',
			element: <TermsOfService />,
		},
		{
			path: '/404',
			element: <p>Page not found.</p>,
		},
		{
			path: '*',
			element: <Navigate to="/404" replace />,
		},
	]);

	return routes;
}
